import React from 'react';
import { FormRenderProps } from 'react-final-form';
import { ApplePayConfig } from '../../resources/CreditCard';
import { ICreditCard, ICreditCardData } from '../../models/CreditCard';
import { IApplePayLineItems, skipWhenPayingWithApplePay } from '../../utils/applePay';
import { creditCard, creditCardExpiration, maxLength, minLength, number, postalCode, required } from '../../validation';
import SavedCardPicker from '../CreditCard/SavedCardPicker';
import { renderCheckboxInput, renderTextInput } from '../Form/RenderForm';
import State from '../Form/State';
import ApplePayButton from './ApplePayButton';

interface IPaymentInformationProps {
  form: FormRenderProps<any>
  loggedIn: boolean
  acceptApplePay: boolean
  applePayConfig?: ApplePayConfig|null
  applePayLineItems?: IApplePayLineItems
  savedCards: ICreditCard[]
  selectedSavedCard: ICreditCard
  onCreditCardSelect: (card: ICreditCard) => void
  onAddCreditCard: () => void
  addingCreditCard: boolean
  loadingSavedCreditCards: boolean
  onMobilePaymentRequest?: (mobilePaymentRequest: PaymentRequest) => void
}

export interface IPaymentInformationData {
  cc?: ICreditCardData
}

const optionalForApplePay = (validators: Array<(value: any, allValues?: any, meta?: any) => any>) =>
  validators.map(skipWhenPayingWithApplePay);

const PaymentInformation: React.FC<IPaymentInformationProps> = props =>
  <>
    {props.acceptApplePay && props.applePayConfig?.enabled && props.applePayLineItems &&
      <ApplePayButton
        merchantIdentifier={props.applePayConfig.merchantIdentifier}
        description={props.applePayConfig.displayName || 'Reliance Payment'}
        lineItems={props.applePayLineItems}
        form={props.form}
        onSubmit={(paymentRequest: PaymentRequest) =>
          props.onMobilePaymentRequest(paymentRequest)
        } />
    }

    {(props.loadingSavedCreditCards || (props.savedCards||[]).length > 0) &&
      <SavedCardPicker
        loading={props.loadingSavedCreditCards}
        onCreditCardSelect={card => {
          props.form.form.mutators.useSavedCreditCard()
          props.onCreditCardSelect(card)
        }}
        selectedCard={props.selectedSavedCard}
        savedCards={props.savedCards} />
    }

    {(props.savedCards||[]).length > 0 ?
      <section className="row pt-2 pb-4 mr-lg-1">
        <h4 className="col-lg-10 col-8 mb-0 d-flex align-self-center">Add a new card</h4>
        <div className="col-lg-2 col-4 px-2 px-lg-2 pr-3 d-flex align-self-center bodysm">
          <button className="btn btn-secondary btn-sm btn-block text-light" type="button" onClick={props.onAddCreditCard}>
            Add card
          </button>
        </div>
      </section> :
      <h4 className="mb-0 pt-3">
        Payment information
      </h4>
    }

    {((props.savedCards||[]).length == 0 || props.addingCreditCard) &&
      <>
        <p className="mt-0 bodysm">We accept Visa, Mastercard, Discover, or American Express debit or check cards</p>

        <section className="row">
          <div className="col-12 form-label-group">
            {renderTextInput(
              'cc.nameOnCard',
              'cc.nameOnCard',
              'Name on card',
              'Name on card',
              optionalForApplePay([required])
            )}
          </div>
          <div className="col-12 col-lg-6 form-label-group">
            {renderTextInput(
              'cc.cardNumber',
              'cc.cardNumber',
              'Card number',
              'Card number',
              optionalForApplePay([required, creditCard])
            )}
          </div>
          <div className="col-12 col-lg-3 form-label-group">
            {renderTextInput(
              'cc.expirationDate',
              'cc.expirationDate',
              'Exp. Date (MM/YY)',
              'Exp. Date (MM/YY)',
              optionalForApplePay([required, creditCardExpiration])
            )}
          </div>
          <div className="col-12 col-lg-3 form-label-group">
            {renderTextInput(
              'cc.cardSecurityCode',
              'cc.cardSecurityCode',
              'CVC',
              'CVC',
              optionalForApplePay([required, number, maxLength(4), minLength(3)])
            )}
          </div>
        </section>

        <section className="row">
          <div className="col-12">
            <button
              className="btn btn-action btn-block text-light"
              type="button"
              onClick={props.form.form.mutators.copyPersonalToBilling}>
              Use same address as above for billing address
            </button>
            <br/>
          </div>
        </section>

        <section className="row">
          <div className="col-12 form-label-group">
            {renderTextInput(
              'cc.address',
              'cc.address',
              'Address',
              'Address',
              optionalForApplePay([required])
            )}
          </div>
          <div className="col-12 col-lg-6 form-label-group">
            {renderTextInput(
              'cc.city',
              'cc.city',
              'City',
              'City',
              optionalForApplePay([required])
            )}
          </div>
          <div className="col-12 col-lg-3 form-label-group">
            <State
              name="cc.state"
              includeCanada={true}
              validationRules={optionalForApplePay([required])} />
          </div>
          <div className="col-12 col-lg-3 form-label-group">
            {renderTextInput(
              'cc.zip',
              'cc.zip',
              'Postal code',
              'Postal code',
              optionalForApplePay([required, postalCode])
            )}
          </div>
          <div className="col-12 form-label-group">
            {renderCheckboxInput(
              'cc.save',
              'cc.save',
              'Save this card for future payments',
              [],
              'bodymd'
            )}
          </div>
        </section>
      </>
    }
  </>

export default PaymentInformation;
